import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Download, ArrowLeft, Copy, ExternalLink, Crown, Zap, Check, Lock,
  Calendar, MapPin, Users, Music, BarChart3, QrCode, Globe
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatDateShort } from '@/utils/date';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to generate QR code image from URL
const generateQRCodeUrl = async (url, authHeader) => {
  try {
    const response = await axios.post(`${API}/generate-qr`, { url }, { headers: authHeader });
    return response.data.qr_code;
  } catch (error) {
    console.error('Error generating QR code:', error);
    return null;
  }
};

export default function QRCodePage() {
  const { getAuthHeader, user, isPro } = useAuth();
  const { language, t } = useLanguage();
  const [qrData, setQrData] = useState(null);
  const [eventQRs, setEventQRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedUrl, setCopiedUrl] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [qrResponse, eventsResponse] = await Promise.all([
          axios.get(`${API}/qrcode`, { headers: getAuthHeader() }),
          axios.get(`${API}/events/qrcodes`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
        ]);
        
        setQrData(qrResponse.data);
        setEventQRs(eventsResponse.data || []);
      } catch (error) {
        console.error('Error loading QR codes');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAuthHeader]);

  const handleDownload = (qrCode, name) => {
    const link = document.createElement('a');
    link.href = qrCode;
    link.download = `qrcode-${user?.dj_name || 'dj'}-${name}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyLink = (url, type) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(type);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  // Download event poster PDF with authentication
  const downloadEventPoster = async (eventId, format) => {
    try {
      const response = await axios.get(
        `${API}/events/${eventId}/poster/${format}`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trackvibes-event-${format}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading poster:', error);
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          alert(json.detail || t('qrcodes', 'download_error'));
        } catch {
          alert(t('qrcodes', 'download_error'));
        }
      } else {
        alert(error.response?.data?.detail || t('qrcodes', 'download_error'));
      }
    }
  };

  // Download DJ poster (free/pro mode) PDF with authentication
  const downloadDjPoster = async (mode, format) => {
    try {
      const response = await axios.get(
        `${API}/dj/poster/${mode}/${format}`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trackvibes-${mode}-${format}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading poster:', error);
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          alert(json.detail || t('qrcodes', 'download_error'));
        } catch {
          alert(t('qrcodes', 'download_error'));
        }
      } else {
        alert(error.response?.data?.detail || t('qrcodes', 'download_error'));
      }
    }
  };

  // Use centralized isPro() from AuthContext
  const userIsPro = isPro();

  const getEventTypeLabel = (type) => {
    const typeLabels = t('events', 'types') || {};
    const labels = {
      'mariage': `💒 ${typeLabels.mariage || 'Mariage'}`,
      'club': `🎉 ${typeLabels.club || 'Club'}`,
      'bar': `🍻 ${typeLabels.bar || 'Bar'}`,
      'anniversaire': `🎂 ${typeLabels.anniversaire || 'Anniversaire'}`,
      'entreprise': `💼 ${typeLabels.entreprise || 'Entreprise'}`,
      'autre': `🎵 ${typeLabels.autre || 'Autre'}`
    };
    return labels[type] || type;
  };

  // Wrapper to use centralized date util with current language
  const localFormatDate = (dateStr) => formatDateShort(dateStr, language);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-neon-cyan animate-pulse font-heading text-2xl">{t('common', 'loading')}</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-[#050505] overflow-x-hidden"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(5,5,5,0.95), rgba(5,5,5,0.9)), url('https://images.unsplash.com/photo-1706670542491-61eeb26510de?w=1200')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {/* Navigation */}
      <nav className="bg-[#0a0a0a]/80 backdrop-blur border-b border-white/10 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-gray-400 hover:text-white px-2 sm:px-4" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 sm:mr-2" />
              <span className="hidden sm:inline">{t('common', 'back')}</span>
            </Button>
          </Link>
          <h1 className="font-heading text-lg sm:text-xl text-white">{t('qrcodes', 'title')}</h1>
          <div className="w-12 sm:w-24" />
        </div>
      </nav>

      <div className="max-w-6xl mx-auto p-4 sm:p-6 pt-8 sm:pt-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-heading text-2xl sm:text-3xl text-white mb-2 neon-text break-words">
            {qrData?.dj_name}
          </h2>
          <p className="text-gray-400 text-sm sm:text-base px-4">
            {userIsPro ? t('qrcodes', 'choose_qr') : t('qrcodes', 'your_qr')}
          </p>
          {/* Plan Badge */}
          <div className={`inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-semibold ${
            userIsPro ? 'bg-neon-pink/20 text-neon-pink' : 'bg-neon-cyan/20 text-neon-cyan'
          }`}>
            {userIsPro ? <Crown className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
            {userIsPro ? t('qrcodes', 'plan_pro') : t('qrcodes', 'plan_free')}
          </div>
        </div>

        {/* QR Codes Grid */}
        <div className={`grid ${userIsPro ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md mx-auto'} gap-6 sm:gap-8`}>
          
          {/* QR Code FREE */}
          <div className="glass rounded-2xl p-4 sm:p-8 text-center border border-neon-cyan/30">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Zap className="w-5 sm:w-6 h-5 sm:h-6 text-neon-cyan" />
              <h3 className="font-heading text-lg sm:text-xl text-white">{t('qrcodes', 'qr_free_title')}</h3>
            </div>
            
            <div className="bg-white/5 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 text-left">
              <p className="text-gray-300 text-sm mb-3">{t('qrcodes', 'included')}</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2 text-gray-400">
                  <Check className="w-4 h-4 text-neon-cyan flex-shrink-0" />
                  <span>{t('qrcodes', 'standard_form')}</span>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <Check className="w-4 h-4 text-neon-cyan flex-shrink-0" />
                  <span>{t('qrcodes', 'realtime_list')}</span>
                </li>
                <li className="flex items-center gap-2 text-gray-400">
                  <Check className="w-4 h-4 text-neon-cyan flex-shrink-0" />
                  <span>{t('qrcodes', 'multi_lang')}</span>
                </li>
              </ul>
            </div>

            {qrData?.free && (
              <>
                <div className="bg-white p-3 sm:p-4 rounded-xl inline-block mb-4 sm:mb-6">
                  <img 
                    src={qrData.free.qr_code} 
                    alt="QR Code Free"
                    className="w-36 h-36 sm:w-48 sm:h-48"
                    data-testid="qr-code-free"
                  />
                </div>

                <div 
                  className="bg-white/5 rounded-lg p-2 sm:p-3 mb-4 sm:mb-6 cursor-pointer hover:bg-white/10 transition-colors overflow-hidden"
                  onClick={() => window.open(qrData.free.url, '_blank')}
                >
                  <p className="text-neon-cyan text-xs break-all overflow-wrap-anywhere">
                    {qrData.free.url}
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  {/* Primary action - Open form */}
                  <a 
                    href={qrData.free.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button
                      className="w-full bg-neon-cyan text-black font-bold uppercase tracking-wider text-sm sm:tracking-widest hover:shadow-[0_0_30px_rgba(0,240,255,0.6)] rounded-full py-2.5 sm:py-3"
                    >
                      <ExternalLink className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{t('qrcodes', 'open_form')}</span>
                    </Button>
                  </a>
                  
                  {/* Secondary actions */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => handleDownload(qrData.free.qr_code, 'free')}
                      variant="outline"
                      className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full text-xs sm:text-sm px-2 sm:px-4"
                      data-testid="download-free-btn"
                    >
                      <Download className="w-4 h-4 mr-1 flex-shrink-0" />
                      <span className="truncate">QR</span>
                    </Button>
                    <Button
                      onClick={() => handleCopyLink(qrData.free.url, 'free')}
                      variant="outline"
                      className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full text-xs sm:text-sm px-2 sm:px-4"
                      data-testid="copy-free-btn"
                    >
                      {copiedUrl === 'free' ? (
                        <><Check className="w-4 h-4 mr-1 flex-shrink-0" /> <span className="truncate">{t('qrcodes', 'copied')}</span></>
                      ) : (
                        <><Copy className="w-4 h-4 mr-1 flex-shrink-0" /> <span className="truncate">{t('qrcodes', 'copy_link')}</span></>
                      )}
                    </Button>
                  </div>
                  
                  {/* Poster download buttons - FREE */}
                  <div className="grid grid-cols-2 gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-white/10">
                    <Button
                      onClick={() => downloadDjPoster('free', 'A4')}
                      variant="outline"
                      className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full text-xs sm:text-sm px-2 sm:px-4"
                    >
                      <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                      <span className="truncate">{t('qrcodes', 'download_a4')}</span>
                    </Button>
                    <Button
                      onClick={() => downloadDjPoster('free', 'A5')}
                      variant="outline"
                      className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full text-xs sm:text-sm px-2 sm:px-4"
                    >
                      <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                      <span className="truncate">{t('qrcodes', 'download_a5')}</span>
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* QR Code PRO */}
          {userIsPro ? (
            <div className="glass rounded-2xl p-4 sm:p-8 text-center border border-neon-pink/30 relative overflow-hidden">
              {/* Pro badge */}
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 bg-gradient-to-r from-neon-pink to-purple-500 text-white text-xs font-bold px-2 sm:px-3 py-1 rounded-full">
                {t('qrcodes', 'recommended')}
              </div>
              
              <div className="flex items-center justify-center gap-2 mb-4 mt-6 sm:mt-0">
                <Crown className="w-5 sm:w-6 h-5 sm:h-6 text-neon-pink" />
                <h3 className="font-heading text-lg sm:text-xl text-white">{t('qrcodes', 'qr_pro_title')}</h3>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 text-left">
                <p className="text-gray-300 text-sm mb-3">{t('qrcodes', 'included')}</p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-gray-400">
                    <Check className="w-4 h-4 text-neon-pink flex-shrink-0" />
                    <span>{t('qrcodes', 'all_free_plan')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-neon-pink">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span className="text-white">{t('qrcodes', 'dj_business_card')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-neon-pink">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span className="text-white">{t('qrcodes', 'tip_option')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-neon-pink">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span className="text-white">{t('qrcodes', 'detailed_stats')}</span>
                  </li>
                  <li className="flex items-center gap-2 text-neon-pink">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span className="text-white">{t('qrcodes', 'unique_event_qr')}</span>
                  </li>
                </ul>
              </div>

              {qrData?.pro && (
                <>
                  <div className="bg-white p-3 sm:p-4 rounded-xl inline-block mb-4 sm:mb-6">
                    <img 
                      src={qrData.pro.qr_code} 
                      alt="QR Code Pro"
                      className="w-36 h-36 sm:w-48 sm:h-48"
                      data-testid="qr-code-pro"
                    />
                  </div>

                  <div 
                    className="bg-white/5 rounded-lg p-2 sm:p-3 mb-4 sm:mb-6 cursor-pointer hover:bg-white/10 transition-colors overflow-hidden"
                    onClick={() => window.open(qrData.pro.url, '_blank')}
                  >
                    <p className="text-neon-pink text-xs break-all overflow-wrap-anywhere">
                      {qrData.pro.url}
                    </p>
                  </div>

                  <div className="flex flex-col gap-3">
                    {/* Primary action - Open form */}
                    <a 
                      href={qrData.pro.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-full"
                    >
                      <Button
                        className="w-full bg-gradient-to-r from-neon-pink to-purple-500 text-white font-bold uppercase tracking-wider text-sm sm:tracking-widest hover:shadow-[0_0_30px_rgba(255,0,153,0.6)] rounded-full py-2.5 sm:py-3"
                      >
                        <ExternalLink className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{t('qrcodes', 'open_form')}</span>
                      </Button>
                    </a>
                    
                    {/* Secondary actions */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        onClick={() => handleDownload(qrData.pro.qr_code, 'pro')}
                        variant="outline"
                        className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full text-xs sm:text-sm px-2 sm:px-4"
                        data-testid="download-pro-btn"
                      >
                        <Download className="w-4 h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">QR</span>
                      </Button>
                      <Button
                        onClick={() => handleCopyLink(qrData.pro.url, 'pro')}
                        variant="outline"
                        className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full text-xs sm:text-sm px-2 sm:px-4"
                        data-testid="copy-pro-btn"
                      >
                        {copiedUrl === 'pro' ? (
                          <><Check className="w-4 h-4 mr-1 flex-shrink-0" /> <span className="truncate">{t('qrcodes', 'copied')}</span></>
                        ) : (
                          <><Copy className="w-4 h-4 mr-1 flex-shrink-0" /> <span className="truncate">{t('qrcodes', 'copy_link')}</span></>
                        )}
                      </Button>
                    </div>
                    
                    {/* Poster download buttons - PRO */}
                    <div className="grid grid-cols-2 gap-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-white/10">
                      <Button
                        onClick={() => downloadDjPoster('pro', 'A4')}
                        variant="outline"
                        className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full text-xs sm:text-sm px-2 sm:px-4"
                      >
                        <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{t('qrcodes', 'download_a4')}</span>
                      </Button>
                      <Button
                        onClick={() => downloadDjPoster('pro', 'A5')}
                        variant="outline"
                        className="border-white/20 text-gray-300 hover:bg-white/10 rounded-full text-xs sm:text-sm px-2 sm:px-4"
                      >
                        <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                        <span className="truncate">{t('qrcodes', 'download_a5')}</span>
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* PRO Card Locked for FREE users */
            <div className="glass rounded-2xl p-4 sm:p-8 text-center border border-white/10 relative overflow-hidden">
              {/* Locked overlay */}
              <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center z-10 p-4 sm:p-6">
                <Lock className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-4" />
                <p className="text-white font-semibold text-base sm:text-lg mb-2">{t('qrcodes', 'pro_version')}</p>
                <p className="text-gray-300 text-xs sm:text-sm mb-4 px-2 sm:px-4 max-w-xs">
                  {t('qrcodes', 'unlock_pro')}
                </p>
                <Link to="/settings?tab=subscription" className="w-full max-w-xs">
                  <Button
                    className="w-full bg-gradient-to-r from-neon-pink to-purple-500 text-white font-bold uppercase tracking-wider text-sm hover:shadow-[0_0_30px_rgba(255,0,153,0.6)] rounded-full py-2.5 sm:py-3"
                    data-testid="upgrade-pro-btn"
                  >
                    <Crown className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{t('qrcodes', 'upgrade_pro')}</span>
                  </Button>
                </Link>
                <p className="text-neon-cyan text-xs mt-3 font-medium px-2">
                  {t('qrcodes', 'trial_info')}
                </p>
              </div>
              
              <div className="flex items-center justify-center gap-2 mb-4 opacity-40">
                <Crown className="w-5 sm:w-6 h-5 sm:h-6 text-gray-500" />
                <h3 className="font-heading text-lg sm:text-xl text-gray-500">{t('qrcodes', 'qr_pro_title')}</h3>
              </div>
              
              <div className="bg-white/5 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6 text-left opacity-40">
                <ul className="space-y-2 text-sm text-gray-500">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span>{t('qrcodes', 'dj_business_card')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span>{t('qrcodes', 'tip_option')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span>{t('qrcodes', 'detailed_stats')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 flex-shrink-0" />
                    <span>{t('qrcodes', 'unique_event_qr')}</span>
                  </li>
                </ul>
              </div>

              {/* Placeholder QR */}
              <div className="bg-gray-800 p-4 rounded-xl inline-block mb-6 w-36 h-36 sm:w-48 sm:h-48 opacity-40">
                <div className="w-full h-full bg-gray-700 rounded flex items-center justify-center">
                  <Lock className="w-10 sm:w-12 h-10 sm:h-12 text-gray-600" />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Event QR Codes Section - PRO Only */}
        {userIsPro && (
          <div className="mt-12 sm:mt-16">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-3">
                <QrCode className="w-5 sm:w-6 h-5 sm:h-6 text-neon-pink" />
                <h3 className="font-heading text-lg sm:text-xl text-white">{t('qrcodes', 'event_qr_title')}</h3>
              </div>
              <Link to="/events" className="w-full sm:w-auto">
                <Button
                  variant="outline"
                  className="w-full sm:w-auto border-neon-pink/30 text-neon-pink hover:bg-neon-pink/10 text-sm"
                >
                  <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">{t('qrcodes', 'manage_events')}</span>
                </Button>
              </Link>
            </div>

            {eventQRs.length === 0 ? (
              <div className="glass rounded-2xl p-6 sm:p-8 text-center border border-white/10">
                <Calendar className="w-10 sm:w-12 h-10 sm:h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 mb-2">{t('qrcodes', 'no_event_qr')}</p>
                <p className="text-gray-500 text-sm mb-6 px-4">
                  {t('qrcodes', 'no_event_qr_hint')}
                </p>
                <Link to="/events">
                  <Button className="bg-neon-pink text-white hover:bg-neon-pink/80">
                    <Calendar className="w-4 h-4 mr-2" />
                    {t('qrcodes', 'create_event_btn')}
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {eventQRs.map((event) => (
                  <div 
                    key={event.id}
                    className={`glass rounded-2xl p-4 sm:p-6 border ${
                      event.status === 'active' 
                        ? 'border-neon-cyan/50 bg-neon-cyan/5' 
                        : 'border-white/10'
                    }`}
                  >
                    {/* Event Header */}
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                      <Badge className={`text-xs ${
                        event.status === 'active' 
                          ? 'bg-neon-cyan text-black' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {event.status === 'active' ? t('qrcodes', 'active') : t('qrcodes', 'draft')}
                      </Badge>
                      {event.requests_count > 0 && (
                        <span className="text-neon-pink text-xs sm:text-sm">
                          {event.requests_count} {event.requests_count > 1 ? t('qrcodes', 'requests_count_plural') : t('qrcodes', 'requests_count')}
                        </span>
                      )}
                    </div>

                    {/* Event Info */}
                    <h4 className="font-semibold text-white mb-2 text-sm sm:text-base break-words">{event.name}</h4>
                    <div className="space-y-1 text-xs sm:text-sm text-gray-400 mb-4">
                      <p className="flex items-center gap-2">
                        <Music className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{getEventTypeLabel(event.event_type)}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 flex-shrink-0" />
                        <span>{localFormatDate(event.date)}</span>
                      </p>
                      {event.location && (
                        <p className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{event.location}</span>
                        </p>
                      )}
                    </div>

                    {/* QR URL */}
                    {event.qr_url && (
                      <div 
                        className="bg-white/5 rounded-lg p-2 mb-4 cursor-pointer hover:bg-white/10 transition-colors overflow-hidden"
                        onClick={() => window.open(event.qr_url, '_blank')}
                      >
                        <p className="text-neon-pink text-xs break-all overflow-wrap-anywhere line-clamp-2">
                          {event.qr_url}
                        </p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      {/* Primary action - Open form */}
                      <a 
                        href={event.qr_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="w-full"
                      >
                        <Button
                          size="sm"
                          className="w-full bg-gradient-to-r from-neon-pink to-purple-500 text-white hover:opacity-90 text-xs sm:text-sm"
                        >
                          <ExternalLink className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{t('qrcodes', 'open_form')}</span>
                        </Button>
                      </a>
                      
                      {/* Copy button */}
                      <Button
                        onClick={() => handleCopyLink(event.qr_url, event.id)}
                        size="sm"
                        variant="outline"
                        className="w-full border-white/20 text-gray-300 hover:bg-white/10 text-xs sm:text-sm"
                      >
                        {copiedUrl === event.id ? (
                          <><Check className="w-3 h-3 mr-1 flex-shrink-0" /> <span>{t('qrcodes', 'copied')}</span></>
                        ) : (
                          <><Copy className="w-3 h-3 mr-1 flex-shrink-0" /> <span>{t('qrcodes', 'copy_link')}</span></>
                        )}
                      </Button>
                    </div>
                    
                    {/* Poster download buttons */}
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <Button
                        onClick={() => downloadEventPoster(event.id, 'A4')}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-gray-300 hover:bg-white/10 text-xs px-2"
                      >
                        <Download className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{t('qrcodes', 'download_a4')}</span>
                      </Button>
                      <Button
                        onClick={() => downloadEventPoster(event.id, 'A5')}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-gray-300 hover:bg-white/10 text-xs px-2"
                      >
                        <Download className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{t('qrcodes', 'download_a5')}</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info box about event QR codes */}
            <div className="mt-6 bg-neon-pink/10 border border-neon-pink/30 rounded-xl p-3 sm:p-4">
              <div className="flex items-start gap-3">
                <BarChart3 className="w-5 h-5 text-neon-pink mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm">{t('qrcodes', 'advance_collection')}</p>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    {t('qrcodes', 'advance_collection_desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="mt-10 sm:mt-12 text-center">
          <h3 className="font-heading text-base sm:text-lg text-white mb-4">{t('qrcodes', 'usage_tips')}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-gray-400 text-xs sm:text-sm">
            <div className="bg-white/5 rounded-lg p-3 sm:p-4">
              <span className="text-neon-cyan font-bold">Free</span> → {t('qrcodes', 'free_tip')}
            </div>
            {userIsPro && (
              <div className="bg-white/5 rounded-lg p-3 sm:p-4">
                <span className="text-neon-pink font-bold">Pro</span> → {t('qrcodes', 'pro_tip')}
              </div>
            )}
            <div className="bg-white/5 rounded-lg p-3 sm:p-4">
              <span className="text-white font-bold">💡</span> {t('qrcodes', 'print_tip')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
